import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Clock, DollarSign, User, Mail, Phone, CheckCircle2, X, Inbox, Hand, Video, Play, Download, Wand2, MessageCircle, ChevronLeft } from 'lucide-react';
import { BookingThreadDialog } from '@/components/readers/BookingThreadDialog';
import { VideoEditDrawer } from '@/components/video-editor/VideoEditDrawer';
import { useToast } from '@/hooks/use-toast';
import {
  fetchMyReaderBookings,
  getMyReaderProfile,
  fetchOpenReaderBookings,
  fetchBookedReaderBookings,
  acceptReaderBooking,
  bookReaderBooking,
  confirmReaderBooking,
  declineReaderBooking,
  withdrawReaderBooking,
  completeReaderBooking,
  reportReaderNoShow,
  askOtherReaders,
  cancelReaderBooking,
  fetchBookingTakes,
  saveBookingTakeEnhanced,
  type ReaderBooking,
  type BookingTake,
} from '@/services/readerApi';
import { useSubscription } from '@/hooks/useSubscription';
import { ReaderSectionHeader } from '@/components/readers/ReaderSectionHeader';
import { computeBookingPrice, NON_MEMBER_PAD } from '@/lib/readerPricing';

const SERVICE_LABEL: Record<string, string> = {
  reader: 'Reader',
  session_director: 'Session Director',
  coach: 'Coach',
};

const money = (c?: number) => `$${((c || 0) / 100).toFixed(2)}`;

function whenLine(b: ReaderBooking): string {
  const d = b.sessionDate || 'Flexible date';
  const t = b.sessionTime ? ` · ${b.sessionTime}` : '';
  const z = b.timezoneLabel ? ` ${b.timezoneLabel}` : '';
  return `${d}${t}${z}`;
}

// "until 4:04 PM": the actual deadline, in the viewer's clock, rather than a
// promise of fifteen minutes that used to be five.
function respondByLine(b: ReaderBooking): string {
  if (!b.readerRespondBy) return '';
  const d = new Date(b.readerRespondBy);
  if (isNaN(d.getTime())) return '';
  return ` until ${d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;
}

const STATUS_STYLE: Record<string, { bg: string; ink: string; label: string }> = {
  // NOT --hj-blue: that token is aliased to the brand yellow, so this pill was
  // yellow text on pale blue.
  requesting: { bg: 'rgb(65 113 249 / 0.12)', ink: 'oklch(0.42 0.14 265)', label: 'Finding a reader' },
  // Paid but not yet accepted. The talent's card is authorized, not charged.
  pending_reader: { bg: 'rgb(234 179 8 / 0.16)', ink: 'oklch(0.5 0.12 85)', label: 'Waiting on the reader' },
  // Legacy: bookings now reopen to the pool rather than terminating here. Kept so
  // any row written before that change still renders with a label.
  declined: { bg: 'var(--hj-beige-deep)', ink: 'var(--hj-beige-ink-2)', label: 'Reader unavailable' },
  booked: { bg: 'rgb(234 179 8 / 0.16)', ink: 'oklch(0.5 0.12 85)', label: 'Booked' },
  completed: { bg: 'rgb(34 197 94 / 0.14)', ink: 'var(--hj-green)', label: 'Completed' },
  expired: { bg: 'var(--hj-beige-deep)', ink: 'var(--hj-beige-ink-2)', label: 'Expired' },
  canceled: { bg: 'var(--hj-beige-deep)', ink: 'var(--hj-beige-ink-2)', label: 'Canceled' },
  refunded: { bg: 'var(--hj-beige-deep)', ink: 'var(--hj-beige-ink-2)', label: 'Refunded' },
};

function StatusPill({ status }: { status?: string }) {
  const s = STATUS_STYLE[status || ''] || { bg: 'var(--hj-beige-deep)', ink: 'var(--hj-beige-ink-2)', label: status || '' };
  return (
    <span style={{ padding: '2px 10px', borderRadius: 999, background: s.bg, color: s.ink, fontSize: 11, fontWeight: 700, letterSpacing: '0.02em' }}>
      {s.label}
    </span>
  );
}

// Beige paper, and every token inside it is paper ink. This was a white card
// (--hj-card) filled with --hj-ink / --hj-ink-2 / --hj-ink-3, which resolve to
// near-WHITE in the dark theme: white text on a white card.
const card: React.CSSProperties = {
  padding: 16, borderRadius: 'var(--hj-r-md)', background: 'var(--hj-beige)',
  border: '1px solid var(--hj-beige-line)', color: 'var(--hj-beige-ink)',
  display: 'flex', flexDirection: 'column', gap: 8,
};
const primaryBtn: React.CSSProperties = {
  padding: '9px 18px', borderRadius: 999, background: 'var(--hj-blue)', border: '1px solid var(--hj-blue)',
  color: 'oklch(0.18 0.005 70)', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
};
const ghostBtn: React.CSSProperties = {
  padding: '8px 16px', borderRadius: 999, background: 'transparent', border: '1px solid var(--hj-beige-line)',
  color: 'var(--hj-beige-ink)', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
};
const metaRow: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--hj-beige-ink-2)' };


// Who is asking. A request card with a fee and no person on it is not something
// anyone can answer. Falls back to initials: casting photos are optional, and a
// broken image is worse than none.
function Requester({ name, avatar }: { name?: string | null; avatar?: string | null }) {
  const [failed, setFailed] = useState(false);
  if (!name && !avatar) return null;
  const initials = (name || '?')
    .split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]?.toUpperCase()).join('') || '?';
  return (
    <div className="flex items-center gap-2" style={{ marginTop: 2 }}>
      {avatar && !failed ? (
        <img
          src={avatar}
          alt=""
          onError={() => setFailed(true)}
          style={{ width: 30, height: 30, borderRadius: '50%', objectFit: 'cover', flexShrink: 0, background: 'var(--hj-beige-deep)' }}
        />
      ) : (
        <span
          className="flex items-center justify-center"
          style={{ width: 30, height: 30, borderRadius: '50%', flexShrink: 0, background: 'var(--hj-beige-deep)', color: 'var(--hj-beige-ink-2)', fontSize: 11.5, fontWeight: 700 }}
        >{initials}</span>
      )}
      <span style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--hj-beige-ink)' }}>{name || 'Someone'}</span>
    </div>
  );
}


// The takes from a booked session, on the booking itself, for whoever is looking
// at it. Same shape as casting's recording tiles: a thumbnail with a play mark,
// a modal player when you tap one, and one download button that hands you the
// original file the phone shot. The web encode plays in the modal once it exists.
function TakeTile({ t, onOpen, active }: { t: BookingTake; onOpen: () => void; active?: boolean }) {
  return (
    <button type="button" onClick={onOpen} className="relative"
      style={{ display: 'block', width: '100%', aspectRatio: '16/9', borderRadius: 10, overflow: 'hidden', padding: 0, cursor: 'pointer', background: '#000', border: active ? '2px solid var(--hj-yellow)' : '1px solid var(--hj-beige-line)' }}>
      {t.thumbnailUrl ? (
        <img src={t.thumbnailUrl} alt={`Take ${t.number}`} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
      ) : t.url ? (
        // No encode yet: let the browser paint the original's first frame.
        <video src={`${t.url}#t=0.1`} muted playsInline preload="metadata" style={{ width: '100%', height: '100%', objectFit: 'contain', pointerEvents: 'none' }} />
      ) : null}
      <span className="absolute inset-0 flex items-center justify-center" style={{ background: 'oklch(0 0 0 / 0.18)' }}>
        <span className="flex items-center justify-center" style={{ width: 44, height: 44, borderRadius: 999, background: 'oklch(1 0 0 / 0.92)' }}>
          <Play className="w-5 h-5" style={{ color: '#0A0A0A', fill: '#0A0A0A', marginLeft: 2 }} />
        </span>
      </span>
      <span className="absolute" style={{ left: 8, bottom: 8, padding: '3px 8px', borderRadius: 6, background: 'oklch(0 0 0 / 0.65)', color: '#fff', fontSize: 11, fontWeight: 700 }}>
        Take {t.number}{t.durationSec ? ` · ${Math.round(t.durationSec)}s` : ''}
      </span>
      {t.status === 'encoding' && <span className="absolute" style={{ right: 8, top: 8, padding: '2px 7px', borderRadius: 6, background: 'oklch(0 0 0 / 0.65)', color: '#fff', fontSize: 10, fontWeight: 700 }}>ENCODING</span>}
      {t.enhanced && <span className="absolute" style={{ right: 8, top: 8, padding: '2px 7px', borderRadius: 6, background: 'var(--hj-yellow)', color: '#0A0A0A', fontSize: 10, fontWeight: 700 }}>ENHANCED</span>}
    </button>
  );
}

function BookingTakes({ bookingId, live }: { bookingId: string; live: boolean }) {
  const [takes, setTakes] = useState<BookingTake[] | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  useEffect(() => {
    let stop = false;
    const load = () => fetchBookingTakes(bookingId).then(t => { if (!stop) setTakes(t); });
    load();
    // While the session is live, new takes and encodes land every few seconds.
    const itv = live ? setInterval(load, 10000) : null;
    return () => { stop = true; if (itv) clearInterval(itv); };
  }, [bookingId, live]);
  // The drawer stays MOUNTED once opened and is only hidden on close, so a trim
  // or audio job keeps running in the background like it does on casting. It
  // used to unmount on close, which killed the job mid-flight.
  const [editing, setEditing] = useState<BookingTake | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  // Which version the player shows when an enhanced copy exists. The original
  // is never replaced: if the edit went wrong, it's one tap away.
  const [showOriginal, setShowOriginal] = useState(false);
  // Escape closes the player, like every other modal.
  useEffect(() => {
    if (!openId) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !editOpen) setOpenId(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [openId, editOpen]);
  if (!takes || takes.length === 0) return null;
  const open = takes.find(t => t.id === openId) || null;
  // What plays: your enhanced copy if you made one and haven't switched to the
  // original, else the encode, else the original.
  const showingEnhanced = !!(open?.enhanced && !showOriginal);
  const playUrl = open ? (showingEnhanced ? open.enhanced!.url : open.url) : null;
  const iconBtn: React.CSSProperties = { width: 34, height: 34, borderRadius: 8, border: '1px solid var(--hj-hairline)', background: 'transparent', color: 'var(--hj-ink-2)', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' };
  return (
    <div style={{ marginTop: 6 }}>
      <p className="m-0" style={{ fontSize: 12, fontWeight: 700, color: 'var(--hj-beige-ink)', marginBottom: 8 }}>Takes from this session · {takes.length}</p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 10, maxWidth: 680 }}>
        {takes.map(t => <TakeTile key={t.id} t={t} onOpen={() => setOpenId(t.id)} />)}
      </div>
      {open && (
        <div className="fixed inset-0 flex items-center justify-center" style={{ zIndex: 60, background: 'oklch(0 0 0 / 0.72)', padding: 20 }} onClick={() => setOpenId(null)}>
          <div className="flex flex-col" style={{ width: '100%', maxWidth: 860, maxHeight: 'calc(100dvh - 40px)', background: 'var(--hj-surface-1)', border: '1px solid var(--hj-hairline)', borderRadius: 'var(--hj-r-md)', overflow: 'hidden' }} onClick={e => e.stopPropagation()}>
            {/* flex-wrap: on a phone the toggle + Enhance + downloads + close overflowed and were clipped. */}
            <div className="flex flex-wrap items-center gap-2 flex-shrink-0" style={{ padding: '12px 16px', borderBottom: '1px solid var(--hj-hairline-soft)' }}>
              <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--hj-ink)' }}>Take {open.number}</span>
              <span className="mono-label" style={{ color: 'var(--hj-ink-3)' }}>
                {open.durationSec ? `${Math.round(open.durationSec)}s` : ''}{open.status === 'encoding' ? ' · original, encoding a web copy' : ''}
              </span>
              {open.enhanced && (
                <span className="inline-flex" style={{ marginLeft: 8, borderRadius: 999, border: '1px solid var(--hj-hairline)', overflow: 'hidden' }}>
                  {(['enhanced', 'original'] as const).map(v => {
                    const on = (v === 'enhanced') === showingEnhanced;
                    return (
                      <button key={v} type="button" onClick={() => setShowOriginal(v === 'original')}
                        style={{ padding: '4px 10px', fontSize: 11.5, fontWeight: 700, fontFamily: 'inherit', cursor: 'pointer', border: 0, background: on ? 'var(--hj-yellow)' : 'transparent', color: on ? '#0A0A0A' : 'var(--hj-ink-2)' }}>
                        {v === 'enhanced' ? 'Enhanced' : 'Original'}
                      </button>
                    );
                  })}
                </span>
              )}
              <div className="flex items-center gap-1.5" style={{ marginLeft: 'auto' }}>
                {/* Trim, clean up the audio, color, background: the same edit
                    drawer the builders use, on this take. */}
                {open.s3Key && (
                  <button type="button" onClick={() => { setEditing(open); setEditOpen(true); }} className="inline-flex items-center gap-1.5"
                    style={{ height: 34, padding: '0 12px', borderRadius: 8, border: '1px solid var(--hj-yellow)', background: 'var(--hj-yellow)', color: '#0A0A0A', fontSize: 12.5, fontWeight: 700, fontFamily: 'inherit', cursor: 'pointer' }}>
                    <Wand2 className="w-4 h-4" /> Enhance
                  </button>
                )}
                {open.enhanced && (
                  <a href={open.enhanced.url} download title="Download the enhanced copy" style={{ ...iconBtn, width: 'auto', padding: '0 10px', gap: 6, fontSize: 12, fontWeight: 600, textDecoration: 'none' }}><Download className="w-4 h-4" /> Enhanced</a>
                )}
                {(open.rawUrl || open.url) && (
                  <a href={open.rawUrl || open.url || undefined} download title="Download the original" style={iconBtn}><Download className="w-4 h-4" /></a>
                )}
                <button type="button" onClick={() => setOpenId(null)} title="Close" style={iconBtn}><X className="w-4 h-4" /></button>
              </div>
            </div>
            <div className="flex items-center justify-center" style={{ background: '#000', flex: 1, minHeight: 0 }}>
              {playUrl && <video key={`${open.id}-${open.status}-${showingEnhanced ? 'enh' : 'orig'}`} src={playUrl} controls autoPlay playsInline poster={showingEnhanced ? undefined : (open.thumbnailUrl || undefined)} style={{ width: '100%', maxHeight: '64vh', display: 'block' }} />}
            </div>
            {takes.length > 1 && (
              <div className="flex gap-2 flex-shrink-0 overflow-x-auto" style={{ padding: 12, borderTop: '1px solid var(--hj-hairline-soft)' }}>
                {takes.map(t => <div key={t.id} style={{ width: 120, flexShrink: 0 }}><TakeTile t={t} active={t.id === open.id} onOpen={() => setOpenId(t.id)} /></div>)}
              </div>
            )}
          </div>
        </div>
      )}
      {editing && editing.s3Key && (
        <VideoEditDrawer
          open={editOpen}
          // Start from whichever version is showing in the player: the enhanced
          // copy to keep stacking edits, or the untouched original to start over.
          video={{ s3Key: (editing.enhanced && !showOriginal) ? editing.enhanced.s3Key : editing.s3Key, url: ((editing.enhanced && !showOriginal) ? editing.enhanced.url : editing.url) || undefined, title: `Take ${editing.number}` }}
          onApplied={(patch) => {
            const takeId = editing.id;
            setEditOpen(false);
            saveBookingTakeEnhanced(bookingId, takeId, { s3Key: patch.s3Key, muxPlaybackId: patch.muxPlaybackId })
              .then(() => fetchBookingTakes(bookingId).then(t => { setTakes(t); setShowOriginal(false); }))
              .catch(() => {});
          }}
          onClose={() => setEditOpen(false)}
        />
      )}
      {editing && !editOpen && (
        <button type="button" onClick={() => { setOpenId(editing.id); setEditOpen(true); }}
          className="inline-flex items-center gap-1.5"
          style={{ marginTop: 8, padding: '6px 12px', borderRadius: 999, border: '1px solid var(--hj-beige-line)', background: 'transparent', color: 'var(--hj-beige-ink)', fontSize: 12, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer' }}>
          <Wand2 className="w-3.5 h-3.5" /> Reopen the editor for Take {editing.number}
        </button>
      )}
    </div>
  );
}

function SectionHeader({ title, count, sub }: { title: string; count: number; sub?: string }) {
  return (
    <div style={{ margin: '28px 0 12px' }}>
      <h2 className="font-hj-display m-0" style={{ fontSize: 20, color: 'var(--hj-ink)', letterSpacing: '-0.01em' }}>
        {title} <span style={{ color: 'var(--hj-ink-3)', fontWeight: 400 }}>· {count}</span>
      </h2>
      {sub && <p className="m-0" style={{ fontSize: 13, color: 'var(--hj-ink-3)', marginTop: 2 }}>{sub}</p>}
    </div>
  );
}

export default function ReaderSessionsPage() {
  const { toast } = useToast();
  const sub = useSubscription();   // drives the fee disclosure on the Book button

  // Coming back from Stripe used to land on a silent page: you paid, and nothing
  // on screen acknowledged it. What to say depends on whether the reader still
  // has to agree, which is why the param carries that rather than just "booked".
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const paid = params.get('paid');
    if (!paid && !params.get('canceled')) return;
    if (paid === 'pending') {
      toast({
        title: 'Payment authorized',
        description: "We've asked your reader to confirm. Your card isn't charged until they do.",
      });
    } else if (paid === 'booked') {
      toast({ title: "You're booked", description: 'Check your email for the room link and a calendar invite.' });
    } else {
      // Backing out of Stripe used to leave the booking behind: it is written before
      // the Checkout session is created, so an abandoned payment sat in My Requests
      // looking like a live request that readers might answer. Withdraw it on the way
      // back, then reload so the list matches reality.
      const abandoned = params.get('id');
      toast({ title: 'Checkout canceled', description: 'Nothing was charged, and the request was withdrawn.' });
      if (abandoned) {
        cancelReaderBooking(abandoned)
          .then(() => window.location.replace(window.location.pathname))
          .catch(() => {});
      }
    }
    window.history.replaceState({}, '', window.location.pathname);
  }, [toast]);
  const navigate = useNavigate();
  const [mine, setMine] = useState<ReaderBooking[]>([]);
  const [open, setOpen] = useState<ReaderBooking[]>([]);
  const [booked, setBooked] = useState<ReaderBooking[]>([]);
  const [isReader, setIsReader] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  // ?tab=reading|take|sent lands on that tab (the header pill links straight in).
  const [tab, setTab] = useState<'sent' | 'take' | 'reading' | null>(() => {
    const t = new URLSearchParams(window.location.search).get('tab');
    return t === 'sent' || t === 'take' || t === 'reading' ? t : null;
  });
  // The messages + sides dialog for one booking.
  const [thread, setThread] = useState<{ id: string; otherName: string } | null>(null);

  // A request the reader has not answered is NOT a booked session. /booked returns
  // anything where they are the chosen reader regardless of status, and card-on-file
  // sets chosenReaderId at request time — so an unaccepted ask sat under "My booked
  // sessions" reading as though they had already agreed to it.
  const awaitingMe = booked.filter(b => b.status === 'pending_reader');
  const confirmed = booked.filter(b => b.status !== 'pending_reader');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const mineRows = await fetchMyReaderBookings().catch(() => []);
      setMine(mineRows);
      try {
        const [openRows, bookedRows] = await Promise.all([
          fetchOpenReaderBookings(),
          fetchBookedReaderBookings(),
        ]);
        setIsReader(true);
        setOpen(openRows);
        setBooked(bookedRows);
      } catch (e: any) {
        if (e?.message === 'not_a_reader') setIsReader(false);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Reader: "I'm available". Books them outright when the actor agreed to be
  // charged on accept and this reader is at or under that price; otherwise it's
  // a raised hand and the actor picks. The toast has to say which one happened.
  const doAccept = async (b: ReaderBooking) => {
    setBusyId(b.id);
    try {
      const r = await acceptReaderBooking(b.id);
      if (r.booked) {
        toast({ title: "You're booked", description: `${b.talentName || 'The actor'} has been charged. The room link and a calendar invite are in your email.` });
      } else {
        toast({ title: "You're on the list", description: 'The actor will pick who to book — we’ll let you know if it’s you.' });
      }
      await load();
    } catch (e: any) {
      const msg = e?.message === 'charge_failed'
        ? "Their card didn't go through, so nothing's booked yet. They've been asked to fix it, and you're first in line."
        : e?.message === 'already_booked' ? 'Another reader took this one.' : e.message;
      toast({ title: 'Could not accept', description: msg, variant: 'destructive' });
      await load();
    } finally { setBusyId(null); }
  };

  // Talent: book a specific available reader → Stripe.
  const doBook = async (bookingId: string, readerId: string) => {
    setBusyId(bookingId + readerId);
    try {
      const { checkoutUrl } = await bookReaderBooking(bookingId, readerId);
      window.location.href = checkoutUrl;
    } catch (e: any) {
      toast({ title: 'Could not start booking', description: e.message, variant: 'destructive' });
      setBusyId(null);
    }
  };

  // Reader accepts a paid request. This is what captures the talent's card.
  const doConfirm = async (b: ReaderBooking) => {
    setBusyId(b.id);
    try {
      await confirmReaderBooking(b.id);
      toast({ title: "You're confirmed", description: 'We sent you both the room link and a calendar invite.' });
      await load();
    } catch (e: any) {
      toast({ title: 'Could not confirm', description: e.message, variant: 'destructive' });
      await load();
    } finally { setBusyId(null); }
  };

  const doDecline = async (b: ReaderBooking) => {
    if (!confirm("Turn down this session? They won't be charged, and it costs you nothing.")) return;
    setBusyId(b.id);
    try {
      await declineReaderBooking(b.id);
      toast({ title: 'Thanks for letting us know', description: "We've released their payment and told them." });
      await load();
    } catch (e: any) {
      toast({ title: 'Could not decline', description: e.message, variant: 'destructive' });
      await load();
    } finally { setBusyId(null); }
  };

  const doWithdraw = async (b: ReaderBooking) => {
    if (!confirm('Cancel this confirmed session? They will be refunded in full.')) return;
    setBusyId(b.id);
    try {
      await withdrawReaderBooking(b.id);
      toast({ title: 'Session canceled', description: "They've been refunded and we've let them know." });
      await load();
    } catch (e: any) {
      toast({ title: 'Could not cancel', description: e.message, variant: 'destructive' });
      await load();
    } finally { setBusyId(null); }
  };

  const doNoShow = async (b: ReaderBooking, against: 'reader' | 'talent') => {
    const msg = against === 'reader'
      ? "Report that your reader didn't turn up? You'll be refunded in full."
      : "Report that they didn't turn up? You waited out the ten minutes, so the session gets paid out to you and they aren't refunded.";
    if (!confirm(msg)) return;
    setBusyId(b.id);
    try {
      const { outcome } = await reportReaderNoShow(b.id, against);
      toast({
        title: outcome === 'refunded' ? "You've been refunded" : "You've been paid",
        description: outcome === 'refunded'
          ? 'It usually lands back on your card within a few days.'
          : "Thanks for holding the time. We've let them know.",
      });
      await load();
    } catch (e: any) {
      toast({ title: 'Could not report', description: e.message, variant: 'destructive' });
    } finally { setBusyId(null); }
  };

  const doAskOthers = async (b: ReaderBooking) => {
    if (!confirm(b.chargeOnAccept
      ? `Send this to the other readers? ${b.chosenReaderName || 'They'} can still take it, but it stops being theirs alone. The first reader who's free at up to $${(b.rateDollars || 0).toFixed(2)}/15 min is booked and the hold already on your card is used then; nothing extra is charged. Anyone pricier shows up for you to pick.`
      : `Send this to the other readers? ${b.chosenReaderName || 'They'} can still take it, but it stops being theirs alone. Your card hold is released either way.`)) return;
    setBusyId(b.id);
    try {
      await askOtherReaders(b.id);
      toast({ title: 'Sent to the other readers', description: "We'll let you know the moment someone's booked or raises a hand." });
      await load();
    } catch (e: any) {
      toast({ title: 'Could not open it up', description: e.message, variant: 'destructive' });
    } finally { setBusyId(null); }
  };

  const doComplete = async (b: ReaderBooking) => {
    setBusyId(b.id);
    try {
      const { payoutStatus } = await completeReaderBooking(b.id);
      toast({ title: 'Marked complete', description: payoutStatus === 'transferred' ? 'Payout sent to the reader.' : 'Payout is held until the reader connects Stripe.' });
      await load();
    } catch (e: any) {
      toast({ title: 'Could not complete', description: e.message, variant: 'destructive' });
    } finally { setBusyId(null); }
  };

  const doCancel = async (b: ReaderBooking) => {
    setBusyId(b.id);
    try {
      await cancelReaderBooking(b.id);
      toast({ title: b.status === 'booked' ? 'Canceled & refunded' : 'Request canceled' });
      await load();
    } catch (e: any) {
      toast({ title: 'Could not cancel', description: e.message === 'session_started' ? "This session has already started, so it can't be cancelled. If your reader didn't show up, report that instead." : e.message, variant: 'destructive' });
      await load();
    } finally { setBusyId(null); }
  };

  // Live means something can still happen on it; everything else is history.
  const LIVE = new Set(['requesting', 'charging', 'pending_reader', 'booked']);
  const isLive = (b: ReaderBooking) => LIVE.has(b.status || '');
  const byNewest = (x: ReaderBooking, y: ReaderBooking) => new Date(y.createdAt || 0).getTime() - new Date(x.createdAt || 0).getTime();
  const mineLive = mine.filter(isLive);
  const pastMine = mine.filter(b => !isLive(b)).sort(byNewest);
  const readingLive = confirmed.filter(isLive);
  const pastReading = confirmed.filter(b => !isLive(b)).sort(byNewest);

  // The bubble is what's open on each tab: a request still moving, a request to
  // take, or a session that still needs you (an ask to answer or one to run).
  type TabKey = 'sent' | 'take' | 'reading';
  // Same header action as /readers, so the header itself does not change between
  // tabs — only the underline does.
  const [hasReaderProfile, setHasReaderProfile] = useState<boolean | null>(null);
  useEffect(() => {
    getMyReaderProfile().then(r => setHasReaderProfile(!!r.profile)).catch(() => setHasReaderProfile(false));
  }, []);

  const tabs: { key: TabKey; label: string; open: number }[] = [
    { key: 'sent', label: 'Requests I sent', open: mineLive.length + mine.reduce((n, b) => n + (b.unreadMessages || 0), 0) },
    ...(isReader ? [
      { key: 'take' as const, label: 'Requests I can take', open: open.length },
      { key: 'reading' as const, label: "Sessions I'm reading", open: awaitingMe.length + readingLive.length + confirmed.reduce((n, b) => n + (b.unreadMessages || 0), 0) },
    ] : []),
  ];
  // Land on whatever needs you first; a click overrides that for the visit.
  const defaultTab: TabKey = awaitingMe.length ? 'reading' : open.length ? 'take' : mineLive.length ? 'sent' : readingLive.length ? 'reading' : isReader ? 'take' : 'sent';
  const activeTab: TabKey = tab && tabs.some(t => t.key === tab) ? tab : defaultTab;

  // Messages + sides live on every booking that has a reader attached.
  const messagesBtn = (b: ReaderBooking, otherName: string) => (
    (b.status === 'pending_reader' || b.status === 'booked' || b.status === 'completed') ? (
      <button type="button" style={ghostBtn} disabled={busyId === b.id} onClick={() => setThread({ id: b.id, otherName })}>
        <span className="inline-flex items-center gap-1.5">
          <MessageCircle className="w-3.5 h-3.5" /> Messages{b.documentCount ? ` · ${b.documentCount} ${b.documentCount === 1 ? 'file' : 'files'}` : ''}
          {!!b.unreadMessages && <span className="inline-flex items-center justify-center" style={{ minWidth: 18, height: 18, padding: '0 5px', borderRadius: 999, background: 'hsl(var(--destructive))', color: 'white', fontSize: 10.5, fontWeight: 700 }}>{b.unreadMessages}</span>}
        </span>
      </button>
    ) : null
  );

  const mineCard = (b: ReaderBooking) => (
                <div key={b.id} style={card}>
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <strong style={{ fontSize: 15, color: 'var(--hj-beige-ink)' }}>
                      {SERVICE_LABEL[b.serviceType] || b.serviceType}{b.chosenReaderName ? ` · ${b.chosenReaderName}` : ''}
                    </strong>
                    <StatusPill status={b.status} />
                  </div>
                  <div style={metaRow}><Clock className="w-3.5 h-3.5" /> {whenLine(b)} · {b.sessionLength} min</div>

                  {/* Available readers to choose from (while still requesting) */}
                  {b.status === 'requesting' && (b.acceptances?.length ?? 0) > 0 && (
                    <div style={{ display: 'grid', gap: 6, marginTop: 4 }}>
                      <p className="m-0" style={{ fontSize: 12, fontWeight: 700, color: 'var(--hj-beige-ink)' }}>Available now — pick one:</p>
                      {(b.acceptances || []).map(a => {
                        // What Stripe will ACTUALLY charge. This button used to read
                        // the reader's base rate, so it said "Book — $25" and then
                        // took $30: the non-member fee only appeared after the card
                        // was charged. Same helper the booking dialog uses, so the
                        // two surfaces cannot disagree.
                        const price = computeBookingPrice(a.rateDollars, Math.ceil(b.sessionLength / 15), {
                          isSubscriber: sub.isSubscriber,
                          tier: sub.tier,
                          sessionsRemaining: sub.readerSessionsRemaining,
                        });
                        return (
                          <div key={a.readerId} style={{ padding: '10px 12px', borderRadius: 'var(--hj-r-sm)', border: '1px solid var(--hj-beige-line)' }}>
                            <div className="flex items-center justify-between gap-3">
                              <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--hj-beige-ink)' }}>{a.readerName}</span>
                              <button type="button" style={primaryBtn} disabled={busyId === b.id + a.readerId} onClick={() => doBook(b.id, a.readerId)}>
                                {busyId === b.id + a.readerId ? 'Starting…' : `Book — $${price.total.toFixed(2)}`}
                              </button>
                            </div>
                            <p className="m-0" style={{ marginTop: 5, fontSize: 11.5, color: 'var(--hj-beige-ink-2)' }}>
                              ${a.rateDollars.toFixed(2)}/15 min &middot; {b.sessionLength} min
                              {price.padApplied
                                ? <> &middot; plus a ${NON_MEMBER_PAD} non-member fee</>
                                : price.padWaived
                                  ? <> &middot; non-member fee waived with your membership</>
                                  : null}
                            </p>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {b.status === 'pending_reader' && b.needsActorChoice && (
                    <p className="m-0" style={{ fontSize: 12.5, color: 'var(--hj-beige-ink)' }}>
                      <strong>{b.chosenReaderName || 'Your reader'} {b.actorChoiceReason === 'declined' ? "can't make it." : "hasn't answered yet."}</strong>{' '}
                      Nothing is charged; the hold on your card stays until you decide. Send it to the other readers (the first one free at your price is booked and the hold is used then), or cancel and the hold drops off.
                      {b.actorChoiceReason !== 'declined' && <> {b.chosenReaderName || 'They'} can still say yes in the meantime.</>}
                    </p>
                  )}
                  {b.status === 'pending_reader' && !b.needsActorChoice && (
                    <p className="m-0" style={{ fontSize: 12.5, color: 'var(--hj-beige-ink-2)' }}>
                      Waiting on {b.chosenReaderName || 'your reader'} to confirm{respondByLine(b)}. Your card is authorized but <strong>not charged</strong> until they say yes. If we don’t hear back, we’ll check with you before asking anyone else. Don’t want to wait? Send it to the other readers now, or <a href="/readers" style={{ color: 'var(--hj-beige-ink)' }}>browse the other readers</a>.
                    </p>
                  )}
                  {b.status === 'declined' && (
                    <p className="m-0" style={{ fontSize: 12.5, color: 'var(--hj-beige-ink-2)' }}>
                      {b.chosenReaderName || 'That reader'} couldn't make it, so you weren't charged. The hold will disappear from your statement in a day or two.
                    </p>
                  )}
                  {b.status === 'requesting' && b.reopenedAt && (
                    <p className="m-0" style={{ fontSize: 12.5, color: 'var(--hj-beige-ink-2)' }}>
                      This is in front of all the readers now. You haven’t been charged.{' '}
                      {b.chargeOnAccept && b.rateDollars
                        ? <>The first reader who’s free at up to <strong>${b.rateDollars.toFixed(2)}/15 min</strong> (what you agreed to) is booked and {b.holdCarried ? 'the hold on your card is used then' : 'your card is charged then'}. Anyone pricier shows up here for you to pick.</>
                        : <>You’ll pick and pay once someone’s available.</>}
                    </p>
                  )}
                  {b.status === 'expired' && (
                    <p className="m-0" style={{ fontSize: 12.5, color: 'var(--hj-beige-ink-2)' }}>
                      Nobody picked this one up in time, so it closed and you weren&apos;t charged.{' '}
                      <Link to="/readers" style={{ color: 'var(--hj-beige-ink)', fontWeight: 600 }}>Ask again</Link>
                    </p>
                  )}
                  {b.status === 'canceled' && (
                    <p className="m-0" style={{ fontSize: 12.5, color: 'var(--hj-beige-ink-2)' }}>
                      This request was withdrawn. You weren&apos;t charged.
                    </p>
                  )}
                  {b.status === 'requesting' && (b.acceptances?.length ?? 0) === 0 && (
                    <p className="m-0" style={{ fontSize: 12.5, color: 'var(--hj-beige-ink-2)' }}>Waiting on readers to respond — we’ll notify you the moment someone’s available.</p>
                  )}
                  {b.status === 'booked' && <div style={metaRow}><DollarSign className="w-3.5 h-3.5" /> Paid {money(b.totalCents)}</div>}
                  {b.status === 'booked' && b.roomUrl && (
                    <a href={b.roomUrl} target="_blank" rel="noopener noreferrer"
                       style={{ ...primaryBtn, display: 'inline-flex', alignItems: 'center', gap: 6, textDecoration: 'none', alignSelf: 'flex-start', marginTop: 2 }}>
                      <Video className="w-4 h-4" /> Join the session
                    </a>
                  )}
                  {(b.status === 'booked' || b.status === 'completed') && <BookingTakes bookingId={b.id} live={b.status === 'booked'} />}

                  <div className="flex gap-2 flex-wrap">
                    {messagesBtn(b, b.chosenReaderName || 'your reader')}
                    {/* pending_reader too: waiting on someone else is not a reason to
                        be stuck with a hold on your card. */}
                    {b.status === 'pending_reader' && (
                      <button type="button" style={primaryBtn} disabled={busyId === b.id} onClick={() => doAskOthers(b)}>
                        {busyId === b.id ? 'Sending…' : 'Send to the other readers'}
                      </button>
                    )}
                    {(b.status === 'requesting' || b.status === 'pending_reader') && (
                      <button type="button" style={ghostBtn} disabled={busyId === b.id} onClick={() => doCancel(b)}>
                        <span className="inline-flex items-center gap-1.5"><X className="w-3.5 h-3.5" /> {busyId === b.id ? 'Canceling…' : b.status === 'pending_reader' && !b.chargeOnAccept ? 'Cancel & release hold' : 'Cancel request'}</span>
                      </button>
                    )}
                    {b.status === 'booked' && (
                      <>
                        <button type="button" style={primaryBtn} disabled={busyId === b.id} onClick={() => doComplete(b)}>
                          {busyId === b.id ? 'Confirming…' : 'Mark session complete'}
                        </button>
                        {/* Once the session has started there is no cancelling it; a
                            reader who didn't show is reported instead. */}
                        {!(b.startsAt && new Date(b.startsAt).getTime() <= Date.now()) && (
                          <button type="button" style={ghostBtn} disabled={busyId === b.id} onClick={() => doCancel(b)}>Cancel & refund</button>
                        )}
                        <button type="button" style={ghostBtn} disabled={busyId === b.id} onClick={() => doNoShow(b, 'reader')}>
                          Reader didn't show
                        </button>
                      </>
                    )}
                  </div>
                </div>
  );

  const openCard = (b: ReaderBooking) => (
                    <div key={b.id} style={card}>
                      <div className="flex items-center justify-between gap-3 flex-wrap">
                        <strong style={{ fontSize: 15, color: 'var(--hj-beige-ink)' }}>{SERVICE_LABEL[b.serviceType] || b.serviceType}</strong>
                        <span style={{ ...metaRow, color: 'var(--hj-beige-ink)', fontWeight: 700, fontSize: 11 }}>OPEN</span>
                      </div>
                      <Requester name={b.talentName} avatar={b.talentAvatar} />
                      <div style={metaRow}><Clock className="w-3.5 h-3.5" /> {whenLine(b)} · {b.sessionLength} min</div>
                      <div style={metaRow}><DollarSign className="w-3.5 h-3.5" /> You’d earn <strong style={{ color: 'var(--hj-beige-ink)' }}>{money(b.readerPayoutCents)}</strong>{b.instantBook ? '' : ' if booked'}</div>
                      {b.position && <div style={metaRow}>{b.position}</div>}
                      {/* What the button does has to be on the card. With the actor's
                          card on file, tapping is the booking, not an offer. */}
                      <p className="m-0" style={{ fontSize: 12.5, color: 'var(--hj-beige-ink-2)' }}>
                        {b.instantBook
                          ? 'They’ve agreed to the price and their card is on file. First to accept is booked, on the spot.'
                          : 'They pick from everyone who’s free. Saying you’re available isn’t a commitment.'}
                      </p>
                      <div>
                        <button type="button" style={primaryBtn} disabled={busyId === b.id} onClick={() => doAccept(b)}>
                          <span className="inline-flex items-center gap-1.5"><Hand className="w-4 h-4" /> {busyId === b.id ? 'Sending…' : b.instantBook ? 'Take this session' : "I'm available"}</span>
                        </button>
                      </div>
                    </div>
  );

  const bookedCard = (b: ReaderBooking) => (
                    <div key={b.id} style={card}>
                      <div className="flex items-center justify-between gap-3 flex-wrap">
                        <strong style={{ fontSize: 15, color: 'var(--hj-beige-ink)' }}>{SERVICE_LABEL[b.serviceType] || b.serviceType}</strong>
                        <StatusPill status={b.status} />
                      </div>
                      <div style={metaRow}><Clock className="w-3.5 h-3.5" /> {whenLine(b)} · {b.sessionLength} min</div>
                      <Requester name={b.talentName} avatar={(b as any).talentAvatar} />
                      {b.talentEmail && <div style={metaRow}><Mail className="w-3.5 h-3.5" /> <a href={`mailto:${b.talentEmail}`} style={{ color: 'var(--hj-beige-ink)', fontWeight: 600 }}>{b.talentEmail}</a></div>}
                      {b.talentPhone && <div style={metaRow}><Phone className="w-3.5 h-3.5" /> <a href={`tel:${b.talentPhone}`} style={{ color: 'var(--hj-beige-ink)', fontWeight: 600 }}>{b.talentPhone}</a></div>}
                      {b.notes && <div style={{ ...metaRow, color: 'var(--hj-beige-ink-2)' }}>“{b.notes}”</div>}
                      <div style={metaRow}><DollarSign className="w-3.5 h-3.5" /> You earn {money(b.readerPayoutCents)}</div>
                      {/* Paid, but not yours until you say yes. */}
                      {b.status === 'pending_reader' && (
                        <div>
                          <p className="m-0" style={{ fontSize: 12.5, color: 'var(--hj-beige-ink-2)', marginBottom: 8 }}>
                            They asked for you and are waiting{respondByLine(b)}. Nothing is charged until you accept, and turning it down costs you nothing.
                          </p>
                          <div className="flex items-center gap-2 flex-wrap">
                            <button type="button" style={primaryBtn} disabled={busyId === b.id} onClick={() => doConfirm(b)}>
                              <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4" /> {busyId === b.id ? 'Confirming…' : "Yes, I'll take it"}</span>
                            </button>
                            <button type="button" style={ghostBtn} disabled={busyId === b.id} onClick={() => doDecline(b)}>
                              <span className="inline-flex items-center gap-1.5"><X className="w-4 h-4" /> Can't make it</span>
                            </button>
                            {messagesBtn(b, b.talentName || 'the actor')}
                          </div>
                        </div>
                      )}
                      {b.status === 'booked' && b.roomUrl && (
                        <a href={b.roomUrl} target="_blank" rel="noopener noreferrer"
                           style={{ ...primaryBtn, display: 'inline-flex', alignItems: 'center', gap: 6, textDecoration: 'none', alignSelf: 'flex-start' }}>
                          <Video className="w-4 h-4" /> Join the session
                        </a>
                      )}
                      {b.status === 'completed' && <div>{messagesBtn(b, b.talentName || 'the actor')}</div>}
                      {(b.status === 'booked' || b.status === 'completed') && <BookingTakes bookingId={b.id} live={b.status === 'booked'} />}
                      {b.status === 'booked' && (
                        <div className="flex items-center gap-2 flex-wrap">
                          <button type="button" style={primaryBtn} disabled={busyId === b.id} onClick={() => doComplete(b)}>
                            <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4" /> {busyId === b.id ? 'Releasing…' : 'Mark complete & get paid'}</span>
                          </button>
                          <button type="button" style={ghostBtn} disabled={busyId === b.id} onClick={() => doWithdraw(b)}>
                            <span className="inline-flex items-center gap-1.5"><X className="w-4 h-4" /> Can't make it after all</span>
                          </button>
                          <button type="button" style={ghostBtn} disabled={busyId === b.id} onClick={() => doNoShow(b, 'talent')}>
                            They didn't show &mdash; pay me
                          </button>
                          {messagesBtn(b, b.talentName || 'the actor')}
                        </div>
                      )}
                    </div>
  );

  const empty = (msg: string) => (
    <div style={{ ...card, alignItems: 'center', textAlign: 'center', color: 'var(--hj-beige-ink-2)', padding: 28 }}>
      <Inbox className="w-6 h-6" style={{ opacity: 0.5 }} />
      <p className="m-0" style={{ fontSize: 13.5 }}>{msg}</p>
    </div>
  );

  return (
    // Same shell as /readers (1440 + identical padding) so the header and tabs are
    // the same size and in the same place on both. Switching tabs should move the
    // underline, not resize the page. The list itself stays a readable column.
    <main className="mx-auto" style={{ maxWidth: 1440, padding: '0 16px 96px', background: 'var(--hj-bg)', color: 'var(--hj-ink)', minHeight: 'calc(100vh - 68px)' }}>
      {/* Breadcrumb — identical to /readers so the top of the page does not shift
          when you switch tabs. */}
      <div className="flex items-center gap-2.5" style={{ padding: '20px 0 10px', fontSize: 13 }}>
        <button
          type="button"
          onClick={() => navigate('/')}
          className="inline-flex items-center gap-1.5"
          style={{ background: 'transparent', border: 0, color: 'var(--hj-ink-3)', cursor: 'pointer', padding: 0, fontFamily: 'inherit', fontSize: 13 }}
        >
          <ChevronLeft size={12} strokeWidth={2} />
          Home
        </button>
        <span style={{ color: 'var(--hj-ink-4)' }}>/</span>
        <span style={{ color: 'var(--hj-ink)' }}>Readers</span>
      </div>

      {/* Same header and tabs as /readers. The tab that gets you here says "My
          requests", so this page says it too — it used to land on "Reader
          sessions", which read as a different place entirely. */}
      <ReaderSectionHeader
        active="requests"
        actions={(
          <button
            type="button"
            onClick={() => navigate('/readers/profile')}
            className="inline-flex items-center"
            style={{
              padding: '8px 16px', borderRadius: 999, background: 'var(--hj-beige-ink)',
              border: '1px solid var(--hj-beige-ink)', color: 'var(--hj-beige)', fontSize: 13,
              fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer', minHeight: 44,
            }}
          >
            {hasReaderProfile ? 'My reader profile' : 'Become a reader'}
          </button>
        )}
        requestCount={tabs.reduce((n, t) => n + (t.open || 0), 0)}
      />

      {loading ? (
        <p style={{ marginTop: 24, color: 'var(--hj-ink-3)' }}>Loading…</p>
      ) : (
        <>
          {/* Tabs, one per role you're playing on this page, with a bubble on any
              that has something open. Stacked sections buried a live ask under a
              stale expired row and an empty inbox. */}
          {tabs.length > 1 && (
            <div className="flex items-center gap-2 flex-wrap" style={{ margin: '22px 0 4px' }}>
              {tabs.map(t => {
                const active = t.key === activeTab;
                return (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => setTab(t.key)}
                    className="relative inline-flex items-center gap-2"
                    style={{
                      padding: '8px 16px', borderRadius: 999, minHeight: 40, fontSize: 13, fontFamily: 'inherit', cursor: 'pointer',
                      background: active ? 'var(--hj-ink)' : 'transparent',
                      border: `1px solid ${active ? 'var(--hj-ink)' : 'var(--hj-hairline)'}`,
                      color: active ? 'var(--hj-bg)' : 'var(--hj-ink-2)',
                      fontWeight: active ? 700 : 500,
                    }}
                  >
                    {t.label}
                    {t.open > 0 && (
                      <span
                        className="inline-flex items-center justify-center"
                        style={{ minWidth: 18, height: 18, padding: '0 5px', borderRadius: 999, background: 'hsl(var(--destructive))', color: 'white', fontSize: 10.5, fontWeight: 700 }}
                      >
                        {t.open}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}

          {activeTab === 'sent' && (
            <>
              <SectionHeader title="Requests I sent" count={mineLive.length} sub="Sessions you asked for. Readers who are free show up here — pick who you want." />
              {mineLive.length === 0
                ? empty('Nothing open. Requests you send show up here.')
                : <div style={{ display: 'grid', gap: 10 }}>{mineLive.map(mineCard)}</div>}
              {pastMine.length > 0 && (
                <details style={{ marginTop: 28 }}>
                  <summary style={{ cursor: 'pointer', fontSize: 14, fontWeight: 600, color: 'var(--hj-ink-2)' }}>Past &amp; closed · {pastMine.length}</summary>
                  <div style={{ display: 'grid', gap: 10, marginTop: 12 }}>{pastMine.map(mineCard)}</div>
                </details>
              )}
            </>
          )}

          {activeTab === 'take' && (
            <>
              <SectionHeader title="Requests I can take" count={open.length} sub="Actors looking for a reader. Each card says whether accepting books you outright or puts you on their list." />
              {open.length === 0
                ? empty('No one is looking for a reader right now. New requests land here and in Discord.')
                : <div style={{ display: 'grid', gap: 10 }}>{open.map(openCard)}</div>}
            </>
          )}

          {activeTab === 'reading' && (
            <>
              {awaitingMe.length > 0 && (
                <>
                  <SectionHeader title="Needs your answer" count={awaitingMe.length} sub="Someone asked for you by name. Say yes and it is yours." />
                  <div style={{ display: 'grid', gap: 10 }}>{awaitingMe.map(bookedCard)}</div>
                </>
              )}
              <SectionHeader title="Sessions I’m reading" count={readingLive.length} sub="Run the session, then mark it complete to release your payout." />
              {readingLive.length === 0
                ? empty(awaitingMe.length ? 'Nothing booked yet beyond the ask above.' : 'Nobody has booked you yet.')
                : <div style={{ display: 'grid', gap: 10 }}>{readingLive.map(bookedCard)}</div>}
              {pastReading.length > 0 && (
                <details style={{ marginTop: 28 }}>
                  <summary style={{ cursor: 'pointer', fontSize: 14, fontWeight: 600, color: 'var(--hj-ink-2)' }}>Past &amp; closed · {pastReading.length}</summary>
                  <div style={{ display: 'grid', gap: 10, marginTop: 12 }}>{pastReading.map(bookedCard)}</div>
                </details>
              )}
            </>
          )}
        </>
      )}
      <BookingThreadDialog
        open={!!thread}
        onClose={() => setThread(null)}
        bookingId={thread?.id || null}
        otherName={thread?.otherName || ''}
        onChanged={() => { load(); }}
      />
    </main>
  );
}
